import { AnimatedModal } from "@/src/components/AnimatedModal";
import {
  DdqlQuery,
  useExtractDdqlMutation,
  usePreviewDdqlImportQuery,
  useDdqlImportTemplateQuery,
} from "@/src/graphql/generated";
import { useEffect, useState } from "react";
import { SpreadSheet } from "../../data_room/data_room_file/FileContent";
import { Button } from "@/src/components/tailwind/Button";
import {
  BotIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FileSearchIcon,
  FileUpIcon,
  LoaderCircle,
} from "lucide-react";
import { ErrorBoundary } from "@sentry/react";
import useGqlClient from "@/src/hooks/useGqlClient";
import { DdqlRow } from "@/src/components/DdqlRow";
import clsx from "clsx";
import Joyride, {
  CallBackProps,
  STATUS,
  TooltipRenderProps,
} from "react-joyride";
import { XCircleIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/solid";
import { useQueryClient } from "@tanstack/react-query";
import {
  DdqlImportConfigProvider,
  useDdqlImportConfig,
  COLUMN_COLORS,
  COLUMN_OPTIONS,
  ROW_OPTIONS,
} from "./DdqlImportConfigContext";
import { columnIndexToLetter } from "@/src/utils/columnConversion";

const steps = [
  {
    target: "#templates",
    title: "Welcome to the question list importer",
    content:
      "Liquid provides extractors for the most common Excel formats to extract question lists, try these first and see if any pull out your list automatically.",
    disableBeacon: true,
    continuous: true,
  },
  {
    target: "#use-ai",
    title: "Extract questions with AI",
    content:
      "If none of our extractors work, you can let our AI figure out how to extract the questions for you",
    disableBeacon: true,
  },
  {
    target: "#view-doc",
    title: "Check against the original",
    content:
      "Click here to view the original document and compare to the import preview",
    disableBeacon: true,
  },
  {
    target: "#edit-later",
    title: "You can always edit more after!",
    content:
      "When you're ready, click here to import the list. You can edit, move and refine the list once it's imported, so don't worry if it's not perfect",
    disableBeacon: true,
  },
];



export function PreviewDdqlModal(props: {
  open: boolean;
  onClose: () => void;
  ddql: DdqlQuery["ddql"];
  sourceFilePresignedUrl: string;
}) {
  if (!props.ddql.importTemplates || props.ddql.importTemplates.length === 0) {
    return null;
  }

  const initialTemplateId = props.ddql.importTemplates[0].id;

  return (
    <DdqlImportConfigProvider initialTemplateId={initialTemplateId}>
      <PreviewDdqlModalContent {...props} />
    </DdqlImportConfigProvider>
  );
}

function PreviewDdqlModalContent(props: {
  open: boolean;
  onClose: () => void;
  ddql: DdqlQuery["ddql"];
  sourceFilePresignedUrl: string;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const extractDdql = useExtractDdqlMutation(client);
  const [useAi, setUseAi] = useState(false);
  const [showFile, setShowFile] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const {
    startRow,
    skippedSheetIndexes,
    highlightConfig,
    resetConfig,
    selectedTemplateId,
    setSelectedTemplateId,
    sectionColumn,
    setSectionColumn,
    questionColumn,
    setQuestionColumn,
    templateSectionColumn,
    setTemplateSectionColumn,
    templateQuestionColumn,
    setTemplateQuestionColumn,
  } = useDdqlImportConfig();

  // Fetch template data to populate initial column values
  const templateQuery = useDdqlImportTemplateQuery(
    client,
    { id: selectedTemplateId },
    { enabled: selectedTemplateId !== "" }
  );

  // Populate column values from template data
  useEffect(() => {
    if (!templateQuery.data) return;

    const { sectionRules, questionRules } = templateQuery.data.ddqlImportTemplate;

    // Set section column to first rule's column, or null if empty
    const templateSection = sectionRules.length > 0 ? sectionRules[0].column : null;
    setSectionColumn(templateSection);
    setTemplateSectionColumn(templateSection);

    // Set question column to first rule's column, or null if empty
    const templateQuestion = questionRules.length > 0 ? questionRules[0].column : null;
    setQuestionColumn(templateQuestion);
    setTemplateQuestionColumn(templateQuestion);
  }, [templateQuery.data, selectedTemplateId, setSectionColumn, setQuestionColumn, setTemplateSectionColumn, setTemplateQuestionColumn]);

  const ONBOARDING_STORAGE_KEY = "ddql_importer_onboarding_shown_v1";

  useEffect(() => {
    if (!props.open) {
      return;
    }
    // Set showFile to true after a short delay (e.g., 300ms)
    const timer = setTimeout(() => {
      setShowFile(true);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [props.open]);

  useEffect(() => {
    if (!props.open) {
      return;
    }
    try {
      const hasSeen = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (!hasSeen || (hasSeen && hasSeen !== "true")) {
        setShowOnboarding(true);
        localStorage.setItem(ONBOARDING_STORAGE_KEY, "true");
      }
    } catch {
      // no-op if localStorage is unavailable
    }
  }, [props.open]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setShowOnboarding(false);
      try {
        localStorage.setItem(ONBOARDING_STORAGE_KEY, "true");
      } catch {
        // ignore storage errors
      }
    }
  };

  if (!props.ddql.importTemplates || props.ddql.importTemplates.length === 0) {
    return null;
  }

  return (
    <AnimatedModal
      size="full"
      open={props.open}
      onClose={() => {
        setShowFile(false);
        resetConfig();
        props.onClose();
      }}
      closeOnOutsideClick={false}
      padding="p-0"
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <AnimatedModal.Header title="Import preview" onClose={props.onClose} />

        <div className="flex flex-1 w-full min-h-0 overflow-hidden">
          {showFile ? (
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <ColumnConfigPanel />
              <div className="flex-1 w-full p-2 min-h-0">
                <ErrorBoundary
                  fallback={
                    <div className="w-full h-full flex items-center justify-center">
                      <p className="font-semibold text-sm text-concrete-600">
                        Failed to load file
                      </p>
                    </div>
                  }
                >
                  <SpreadSheet
                    url={props.sourceFilePresignedUrl}
                    highlightConfig={highlightConfig}
                    height="100%"
                  />
                </ErrorBoundary>
              </div>
            </div>
          ) : null}
          <div className="flex-1 overflow-y-auto no-scrollbar min-h-0">
            <PreviewImport
              ddql={props.ddql}
              useAi={useAi}
            />
          </div>
        </div>

        <div className="flex-shrink-0 p-3 flex justify-between items-center w-full border-t border-concrete-100">
          <Button
            id="view-doc"
            variant="secondary"
            text={showFile ? "Hide original file" : "Show original file"}
            onClick={() => {
              setShowFile(!showFile);
            }}
            icon={FileSearchIcon}
          />

          <TemplateSwitcher
            options={props.ddql.importTemplates!.map((it) => it.id)}
            onTriggerOnboarding={() => setShowOnboarding(true)}
            setUseAi={setUseAi}
            useAi={useAi}
          />

          <Button
            id="edit-later"
            variant="positive"
            text="Import list"
            icon={FileUpIcon}
            loadingText="Importing..."
            isLoading={extractDdql.isPending}
            onClick={() => {
              if (extractDdql.isPending) {
                return;
              }

              extractDdql.mutate(
                {
                  ddqlID: props.ddql.id,
                  templateID: selectedTemplateId,
                  skippedSheetIndexes,
                  overrides: {
                    startRow: startRow > 0 ? startRow - 1 : undefined,
                    sectionRule: sectionColumn !== templateSectionColumn && sectionColumn !== null
                      ? { column: sectionColumn, regex: ".+" }
                      : undefined,
                    questionRule: questionColumn !== templateQuestionColumn && questionColumn !== null
                      ? { column: questionColumn, regex: ".+" }
                      : undefined,
                  }
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({
                      queryKey: ["Ddql", {}],
                    });
                    props.onClose();
                  },
                },
              );
            }}
          />
        </div>
      </div>
      <Joyride
        run={showOnboarding}
        steps={steps}
        tooltipComponent={CustomTooltip}
        continuous={true}
        callback={handleJoyrideCallback}
        locale={{
          last: "Done",
        }}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            overlayColor: "transparent",
            primaryColor: "rgba(92, 5, 210, 1)",
            textColor: "#004a14",
            width: 900,
            zIndex: 1000,
          },
        }}
      />
    </AnimatedModal>
  );
}

function TemplateSwitcher(props: {
  options: string[];
  onTriggerOnboarding: () => void;
  useAi: boolean;
  setUseAi: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { setSelectedTemplateId } = useDdqlImportConfig();
  const [selectedIndex, setSelectedIndex] = useState(0);
  return (
    <div className="flex items-center gap-x-2">
      <button
        type="button"
        onClick={props.onTriggerOnboarding}
        className="hover:text-concrete-500 rounded-md"
        title="Show help"
        aria-label="Show importer help"
      >
        <QuestionMarkCircleIcon className="w-5 h-5 text-concrete-400" />
      </button>
      <p className="text-concrete-700 text-sm">Try a pre-built extractor</p>

      <div
        id="templates"
        className="border border-concrete-100 rounded-md flex items-center"
      >
        <button
          onClick={() => {
            if (selectedIndex === 0) {
              return;
            }

            setSelectedTemplateId(props.options[selectedIndex - 1]);
            setSelectedIndex(selectedIndex - 1);
          }}
          className="p-2.5 hover:bg-concrete-50"
        >
          <ChevronLeftIcon
            className={clsx(
              "w-4 h-4 ",
              selectedIndex > 0 ? "text-concrete-800" : "text-concrete-300",
            )}
          />
        </button>
        <div className="border-x border-concrete-100 p-2.5">
          <p className="text-concrete-800 text-sm">
            {selectedIndex + 1} / {props.options.length}
          </p>
        </div>
        <button
          onClick={() => {
            if (selectedIndex === props.options.length - 1) {
              return;
            }

            setSelectedTemplateId(props.options[selectedIndex + 1]);
            setSelectedIndex(selectedIndex + 1);
          }}
          className="p-2.5 hover:bg-concrete-50"
        >
          <ChevronRightIcon
            className={clsx(
              "w-4 h-4 ",
              selectedIndex < props.options.length - 1
                ? "text-concrete-800"
                : "text-concrete-300",
            )}
          />
        </button>
      </div>
      <p className="text-sm font-semibold text-concrete-600">or</p>

      <Button
        id="use-ai"
        variant="neutral/soft"
        text="Extract with AI"
        icon={BotIcon}
        onClick={() => {
          props.setUseAi(!props.useAi);
        }}
      />
    </div>
  );
}

function PreviewImport(props: {
  ddql: DdqlQuery["ddql"];
  useAi: boolean;
}) {
  const client = useGqlClient();
  const {
    skippedSheetIndexes,
    toggleSkippedSheet,
    startRow,
    selectedTemplateId,
    setSelectedTemplateId,
    sectionColumn,
    questionColumn,
    templateSectionColumn,
    templateQuestionColumn,
  } = useDdqlImportConfig();

  const previewDdqlImport = usePreviewDdqlImportQuery(
    client,
    {
      input: {
        ddqlID: props.ddql.id,
        templateID: props.useAi ? undefined : selectedTemplateId,
        useAi: props.useAi,
        skippedSheetIndexes,
        overrides: {
          startRow: startRow > 0 ? startRow - 1 : undefined,
          sectionRule: sectionColumn !== templateSectionColumn && sectionColumn !== null
            ? { column: sectionColumn, regex: ".+" }
            : undefined,
          questionRule: questionColumn !== templateQuestionColumn && questionColumn !== null
            ? { column: questionColumn, regex: ".+" }
            : undefined,
        },
      },
    },
    {
      enabled: selectedTemplateId !== "",
    },
  );

  useEffect(() => {
    if (!previewDdqlImport.data || previewDdqlImport.isFetching) {
      return;
    }

    if (
      previewDdqlImport.data.previewDdqlExtraction.ddqlImportTemplateID ===
      selectedTemplateId
    ) {
      return;
    }

    setSelectedTemplateId(
      previewDdqlImport.data.previewDdqlExtraction.ddqlImportTemplateID,
    );
  }, [previewDdqlImport.data, selectedTemplateId]);

  if (previewDdqlImport.error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="font-semibold text-sm text-persian-990">
          Failed to load preview
        </p>
      </div>
    );
  }

  if (previewDdqlImport.isLoading || !previewDdqlImport.data) {
    return (
      <div className="flex-1 w-full h-full space-y-2 flex flex-col items-center justify-center">
        <LoaderCircle className="w-10 h-10 text-persian-990 animate-spin" />
        <p className="text-sm font-semibold text-concrete-800">
          Generating import preview...
        </p>
      </div>
    );
  }

  if (
    previewDdqlImport.data.previewDdqlExtraction.ddql.children.length === 0 &&
    previewDdqlImport.data.previewDdqlExtraction.ddql.totalChildQuestions === 0
  ) {
    return (
      <div>
        <div className="flex flex-col  items-center justify-center h-full py-12">
          <div className="bg-concrete-50 rounded-full p-4 mb-4">
            <FileSearchIcon className="w-10 h-10 text-concrete-400" />
          </div>
          <h2 className="text-lg font-semibold text-concrete-800 mb-2">
            No questions extracted
          </h2>
          <p className="text-sm text-concrete-500 text-center max-w-xs">
            We didn&apos;t find any questions to import from this file using the
            selected extractor.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2">
      <div className="border border-concrete-100 rounded-md">
        <DdqlRow
          renderHeader={false}
          navEnabled={false}
          expanded={true}
          ddql={previewDdqlImport.data.previewDdqlExtraction.ddql}
          rootDdql={previewDdqlImport.data.previewDdqlExtraction.ddql}
          onQuestionClick={() => { }}
          showSkip={true}
          skippedSheetIndexes={skippedSheetIndexes}
          onSkipClicked={toggleSkippedSheet}
        />
      </div>
    </div>
  );
}

function ColorSquare({ color }: { color: string }) {
  return (
    <div
      className="w-3 h-3 rounded-sm flex-shrink-0"
      style={{ backgroundColor: color }}
    />
  );
}

function ColumnDropdown(props: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  color: string;
  allowNone?: boolean;
  noneLabel?: string;
}) {
  const showColorSquare = props.value !== null;

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-concrete-950">
        {props.label}
      </label>
      <div className="relative">
        <select
          value={props.value ?? ""}
          onChange={(e) => {
            const val = e.target.value;
            props.onChange(val === "" ? null : Number(val));
          }}
          className={clsx(
            "appearance-none w-full bg-white border border-concrete-200 rounded-md py-2 pr-8 text-sm text-concrete-800 font-medium focus:outline-none focus:ring-2 focus:ring-persian-500 focus:border-transparent cursor-pointer hover:border-concrete-300",
            showColorSquare ? "pl-8" : "pl-3"
          )}
        >
          {props.allowNone && (
            <option value="">{props.noneLabel ?? "None"}</option>
          )}
          {COLUMN_OPTIONS.map((colIndex) => (
            <option key={colIndex} value={colIndex}>
              Column {columnIndexToLetter(colIndex)}
            </option>
          ))}
        </select>
        {showColorSquare && (
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <ColorSquare color={props.color} />
          </div>
        )}

      </div>
    </div>
  );
}

function ColumnConfigPanel() {
  const {
    sectionColumn,
    setSectionColumn,
    subsectionColumn,
    setSubsectionColumn,
    questionColumn,
    setQuestionColumn,
    startRow,
    setStartRow,
  } = useDdqlImportConfig();

  return (
    <div className="bg-concrete-50 border-b border-concrete-200 p-3">
      <div className="flex items-end gap-4 flex-wrap">
        <ColumnDropdown
          label="Section"
          value={sectionColumn}
          onChange={setSectionColumn}
          color={COLUMN_COLORS.section.solid}
          allowNone
          noneLabel="No section"
        />
        <ColumnDropdown
          label="Subsection"
          value={subsectionColumn}
          onChange={setSubsectionColumn}
          color={COLUMN_COLORS.subsection.solid}
          allowNone
          noneLabel="No subsection"
        />
        <ColumnDropdown
          label="Question"
          value={questionColumn}
          onChange={setQuestionColumn}
          color={COLUMN_COLORS.question.solid}
          allowNone
          noneLabel="No questions"
        />
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-concrete-950">
            Start Row
          </label>
          <div className="relative">
            <select
              value={startRow}
              onChange={(e) => setStartRow(Number(e.target.value))}
              className="appearance-none bg-white border border-concrete-200 rounded-md py-2 pl-3 pr-8 text-sm text-concrete-800 font-medium focus:outline-none focus:ring-2 focus:ring-persian-500 focus:border-transparent cursor-pointer hover:border-concrete-300"
            >
              {ROW_OPTIONS.map((row) => (
                <option key={row} value={row}>
                  Row {row}
                </option>
              ))}
            </select>

          </div>
        </div>
      </div>
    </div>
  );
}

function CustomTooltip(props: TooltipRenderProps) {
  const {
    backProps,
    closeProps,
    continuous,
    index,
    primaryProps,
    skipProps,
    step,
    tooltipProps,
  } = props;

  return (
    <div className="bg-white p-3 rounded-md max-w-96" {...tooltipProps}>
      <div className="flex items-center justify-between gap-x-4">
        {step.title && (
          <h4 className="font-semibold text-concrete-800">{step.title}</h4>
        )}
        <button
          onClick={skipProps.onClick}
          className="text-concrete-400 w-5 h-5 hover:text-concrete-500"
        >
          <XCircleIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="text-sm text-concrete-500">{step.content}</div>
      <div className="mt-3 flex items-center justify-between">
        <button
          className={clsx(
            "relative truncate inline-flex items-center gap-x-2 rounded-md text-sm font-semibold ring-1 ring-inset shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2  px-2 py-1.5",
            "ring-concrete-100 bg-white text-concrete-950 hover:bg-concrete-50",
          )}
          onClick={skipProps.onClick}
        >
          {skipProps.title}
        </button>

        <div className="flex items-center gap-x-2">
          {index > 0 && (
            <button
              className={clsx(
                "relative truncate inline-flex items-center gap-x-2 rounded-md text-sm font-semibold ring-1 ring-inset shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2  px-2 py-1.5",
                "bg-persian-200 text-persian-990 ring-persian-200 hover:bg-persian-100 focus-visible:outline-persian-300 active:bg-persian-300",
              )}
              onClick={backProps.onClick}
            >
              {backProps.title}
            </button>
          )}
          {continuous && (
            <button
              className={clsx(
                "relative truncate inline-flex items-center gap-x-2 rounded-md text-sm font-semibold ring-1 ring-inset shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2  px-2 py-1.5",
                "bg-persian-800 text-white ring-persian-300 hover:bg-persian-700/90 focus-visible:outline-persian-800",
              )}
              onClick={primaryProps.onClick}
            >
              {primaryProps.title}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
